import {
  OnvifCredential,
  UnitScanCamera,
  UnitScanCredentialKind,
  UnitScanCredentialRef,
  UnitScanHost,
  UnitScanResult,
  UnitScanSuggested,
} from "./onvifScanInterfaces";

/**
 * Pure helpers for the "update existing cameras" mode of the scan page
 * (Camera Admin `camera-unit-scan/`). Everything here is side-effect free so
 * the request bodies and the table rows can be unit-tested without a DOM.
 */

const DEFAULT_RTSP_PORT = "554";

// ---------------------------------------------------------------------------
// Request bodies
// ---------------------------------------------------------------------------

export interface UnitScanRequestArgs {
  customerId: number | string;
  /** Restrict the scan to these cameras; omitted = every camera with a host. */
  cameraIds?: number[];
  /** Extra logins to try on top of the site's and the cameras' own. */
  credentials?: OnvifCredential[];
  onvifTimeout?: number;
}

/**
 * Body for POST camera-unit-scan/. Typed credentials are optional extras — the
 * site's camera credential and each camera's existing credential are always
 * tried server-side — so rows without a username are dropped and the key is
 * left out entirely when nothing remains.
 */
export const buildUnitScanRequestBody = ({
  customerId,
  cameraIds,
  credentials,
  onvifTimeout,
}: UnitScanRequestArgs): Record<string, unknown> => {
  const body: Record<string, unknown> = { customer_id: Number(customerId) };
  if (cameraIds && cameraIds.length > 0) {
    body.camera_ids = cameraIds;
  }
  const typed = (credentials ?? [])
    .filter((c) => c.username && c.username.trim() !== "")
    .map((c) => ({ username: c.username.trim(), password: c.password ?? "" }));
  if (typed.length > 0) {
    body.credentials = typed;
  }
  if (onvifTimeout != null && Number.isFinite(onvifTimeout)) {
    body.onvif_timeout = onvifTimeout;
  }
  return body;
};

/** Body for POST camera-unit-scan/{task_id}/apply/. */
export const buildUnitScanApplyBody = (
  selectedIds: Record<number, boolean> | number[]
): { camera_ids: number[] } => {
  const ids = Array.isArray(selectedIds)
    ? selectedIds
    : Object.entries(selectedIds)
        .filter(([, checked]) => checked)
        .map(([id]) => Number(id));
  return { camera_ids: ids.filter((id) => Number.isFinite(id)) };
};

// ---------------------------------------------------------------------------
// Labels
// ---------------------------------------------------------------------------

export const CREDENTIAL_KIND_LABELS: Record<UnitScanCredentialKind, string> = {
  site: "site login",
  camera: "camera login",
  typed: "typed",
};

export const credentialKindLabel = (kind: string | null | undefined): string =>
  CREDENTIAL_KIND_LABELS[kind as UnitScanCredentialKind] ?? String(kind ?? "");

/** "admin (site login)" — never includes a password. */
export const describeCredentialRef = (ref: UnitScanCredentialRef): string =>
  `${ref.username || "(no username)"} (${credentialKindLabel(ref.kind)})`;

/** Plain-words text for a failure flag; null for the non-failure flags. */
export const FAILURE_FLAG_LABELS: Record<string, string> = {
  unreachable: "Unreachable",
  auth_failed: "Login rejected",
  not_onvif: "No ONVIF",
  probe_error: "Probe failed",
  no_substream: "No substream",
  smallest_stream_hd: "Smallest stream is 1080p+",
};

/** Field names as they appear in change chips. */
export const CHANGE_FIELD_LABELS: Record<string, string> = {
  ip: "Host",
  host: "Host",
  rtsp: "Port",
  rtsp_port: "Port",
  stream_parameters: "Stream path",
  stream_path: "Stream path",
  credential_id: "Login",
  credential: "Login",
  username: "Login",
};

export const changeFieldLabel = (field: string): string =>
  CHANGE_FIELD_LABELS[field] ?? field.replace(/_/g, " ");

const formatChangeValue = (value: unknown): string => {
  if (value == null || value === "") {
    return "—";
  }
  return String(value);
};

/** "Stream path: /media/video1 → /media/video2" */
export const describeChange = (
  field: string,
  change: [unknown, unknown] | unknown
): string => {
  const pair = Array.isArray(change) ? change : [undefined, change];
  return `${changeFieldLabel(field)}: ${formatChangeValue(
    pair[0]
  )} → ${formatChangeValue(pair[1])}`;
};

// ---------------------------------------------------------------------------
// Streams
// ---------------------------------------------------------------------------

const portSuffix = (port: unknown): string => {
  const text = String(port ?? "").trim();
  return text && text !== DEFAULT_RTSP_PORT ? ` (port ${text})` : "";
};

/** "/media/video2" or "/media/video2 (port 8554)"; "—" when nothing is set. */
export const formatCurrentStream = (
  current: UnitScanCamera["current"]
): string => {
  const path = String(current?.stream_parameters ?? "").trim();
  const port = portSuffix(current?.rtsp);
  if (!path && !port) {
    return "—";
  }
  return `${path || "—"}${port}`;
};

/** "640x360 /media/video2 (port 8554)"; "—" when there is no suggestion. */
export const formatSuggestedStream = (
  suggested: UnitScanSuggested | null | undefined
): string => {
  if (!suggested) {
    return "—";
  }
  const parts = [suggested.resolution, suggested.stream_path]
    .map((part) => String(part ?? "").trim())
    .filter(Boolean);
  const text = parts.join(" ") + portSuffix(suggested.rtsp_port);
  return text.trim() || "—";
};

/** "admin (site login)" for the login the suggestion would set. */
export const formatSuggestedLogin = (
  suggested: UnitScanSuggested | null | undefined,
  credentialRefs: UnitScanCredentialRef[] | undefined
): string => {
  if (!suggested) {
    return "—";
  }
  const ref =
    suggested.credential_index != null
      ? (credentialRefs ?? []).find(
          (r) => r.index === suggested.credential_index
        )
      : undefined;
  const username = suggested.credential_username ?? ref?.username ?? "";
  if (!username) {
    return "—";
  }
  return ref ? `${username} (${credentialKindLabel(ref.kind)})` : username;
};

/** "Hanwha XNV-6080R" from the host's ONVIF device info. */
export const formatUnit = (host: UnitScanHost): string => {
  const parts = [host.onvif?.manufacturer, host.onvif?.model]
    .map((part) => String(part ?? "").trim())
    .filter(Boolean);
  return parts.join(" ") || "—";
};

// ---------------------------------------------------------------------------
// Rows
// ---------------------------------------------------------------------------

export type UnitScanStatusKind = "ok" | "warning" | "error";

export interface UnitScanRow {
  /** Camera id — the table key and what apply/ receives. */
  id: number;
  name: string;
  active: boolean;
  host: string;
  /** reachable / auth / ONVIF, in plain words. */
  statusText: string;
  statusKind: UnitScanStatusKind;
  unit: string;
  currentStream: string;
  suggestedStream: string;
  login: string;
  /** Change chips; empty when up to date or when the host failed. */
  changeChips: string[];
  /** "Up to date" / failure text when there are no chips. */
  changeText: string | null;
  willChange: boolean;
  upToDate: boolean;
  /** No suggestion → the checkbox is disabled. */
  canApply: boolean;
  flags: string[];
  onvifError: string | null;
}

/** The first failure flag on a host, in the order the backend ranks them. */
export const failureFlag = (flags: string[] | undefined): string | null => {
  const order = [
    "unreachable",
    "auth_failed",
    "not_onvif",
    "probe_error",
    "no_substream",
    "smallest_stream_hd",
  ];
  const set = new Set(flags ?? []);
  return order.find((flag) => set.has(flag)) ?? null;
};

/** Status cell from flags + onvif_auth_status. */
export const describeHostStatus = (
  host: UnitScanHost
): { text: string; kind: UnitScanStatusKind } => {
  const flags = new Set(host.flags ?? []);
  if (flags.has("unreachable")) {
    return { text: "Unreachable", kind: "error" };
  }
  if (flags.has("not_onvif")) {
    return { text: "Reachable, no ONVIF", kind: "error" };
  }
  if (flags.has("auth_failed") || host.onvif_auth_status === "auth_failed") {
    return { text: "Reachable, login rejected", kind: "error" };
  }
  if (flags.has("probe_error")) {
    return { text: "Reachable, probe failed", kind: "error" };
  }
  if (host.onvif_auth_status === "success") {
    return { text: "Reachable, ONVIF OK", kind: "ok" };
  }
  if (host.onvif_auth_status) {
    return {
      text: `Reachable, ONVIF ${host.onvif_auth_status}`,
      kind: "warning",
    };
  }
  return { text: "Reachable", kind: "warning" };
};

/**
 * One row per existing camera, in host order then camera order. A host with no
 * `cameras` (should not happen — the scan only probes hosts that back a camera)
 * yields no row.
 */
export const buildUnitScanRows = (
  result: UnitScanResult | null | undefined
): UnitScanRow[] => {
  const rows: UnitScanRow[] = [];
  const credentialRefs = result?.credentials ?? [];
  (result?.hosts ?? []).forEach((host) => {
    const flags = (host.flags ?? []) as string[];
    const status = describeHostStatus(host);
    const failure = failureFlag(flags);
    const unit = formatUnit(host);
    const suggestedStream = formatSuggestedStream(host.suggested);
    const login = formatSuggestedLogin(host.suggested, credentialRefs);
    (host.cameras ?? []).forEach((camera) => {
      const changes = camera.changes ?? {};
      const changeChips = Object.entries(changes).map(([field, change]) =>
        describeChange(field, change)
      );
      const willChange = !camera.up_to_date && changeChips.length > 0;
      let changeText: string | null = null;
      if (changeChips.length === 0) {
        if (failure) {
          changeText = FAILURE_FLAG_LABELS[failure] ?? failure;
        } else if (camera.up_to_date) {
          changeText = "Up to date";
        } else {
          changeText = host.suggested ? "No changes" : "No suggestion";
        }
      }
      rows.push({
        id: camera.id,
        name: camera.name,
        active: camera.active !== false,
        host: camera.current?.ip || host.ip,
        statusText: status.text,
        statusKind: status.kind,
        unit,
        currentStream: formatCurrentStream(camera.current),
        suggestedStream,
        login,
        changeChips,
        changeText,
        willChange,
        upToDate: Boolean(camera.up_to_date),
        canApply: Boolean(host.suggested) && willChange,
        flags,
        onvifError: host.onvif_error ?? null,
      });
    });
  });
  return rows;
};

/** Default selection: every camera the scan would change. */
export const preselectUnitScanRows = (
  rows: UnitScanRow[]
): Record<number, boolean> => {
  const selected: Record<number, boolean> = {};
  rows.forEach((row) => {
    if (row.canApply && row.willChange) {
      selected[row.id] = true;
    }
  });
  return selected;
};

export const countSelected = (selected: Record<number, boolean>): number =>
  Object.values(selected).filter(Boolean).length;
